import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaHome, FaUsers, FaClipboardList, FaCalendarAlt, FaUser, FaComments, FaSignOutAlt, FaBars, FaTimes, FaQuestionCircle, FaVideo } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/layout/Header';
import NotificationDropdown from '../components/notifications/NotificationDropdown';
import MessageBadge from '../components/chat/MessageBadge';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  position: relative;
`;

const Sidebar = styled.aside`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  z-index: 999;
  transform: ${({ $isOpen }) => $isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.3s ease;
  width: 250px;
  padding: 2rem 0;
  
  @media (min-width: 992px) {
    position: fixed;
    transform: none;
    left: 0;
    top: 0;
  }
`;

const Content = styled.main`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
  overflow-y: auto;
  width: 100%;
  margin-left: 0;
  padding-top: 4rem;
  
  @media (min-width: 992px) {
    margin-left: 250px;
    width: calc(100% - 250px);
    padding-top: 1rem;
  }
`;

const ContentHeader = styled.div`
  display: none;
  
  @media (min-width: 992px) {
    display: block;
    padding-bottom: 1rem;
  }
`;

const ContentBody = styled.div`
  padding: 0
  rem;
`;

const Logo = styled.div`
  padding: 0 1.5rem;
  margin-bottom: 2rem;
  font-size: 1.25rem;
  font-weight: bold;
`;

const NavMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
`;

const NavItem = styled.li`
  margin-bottom: 0.5rem;
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.2s ease;
  background-color: ${({ $active }) => $active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const IconWrapper = styled.span`
  font-size: 1.2rem;
  display: flex;
  align-items: center;
`;

const SignOutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  width: 100%;
  text-align: left;
  font-size: 1rem;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const FooterSection = styled.div`
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1rem;
`;

const MobileNavbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 998;
  
  @media (min-width: 992px) {
    display: none;
  }
`;

const MobileMenuToggle = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  
  &:focus {
    outline: none;
  }
`;

const MobileLogoText = styled.div`
  font-weight: bold;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const MobileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
  display: ${({ $isOpen }) => $isOpen ? 'block' : 'none'};
  
  @media (min-width: 992px) {
    display: none;
  }
`;

const MainContentWrapper = styled.div`
  width: 100%;
`;

const PartnerLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  
  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };
  
  const isPathActive = (path) => {
    return location.pathname.startsWith(path);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };
  
  // Close sidebar when route changes
  useEffect(() => {
    closeSidebar();
  }, [location.pathname]);
  
  return (
    <LayoutContainer>
      <MobileNavbar>
        <MobileMenuToggle onClick={toggleSidebar} aria-label="Toggle menu">
          {isSidebarOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuToggle>
        <MobileLogoText>Partner Portal</MobileLogoText>
        <MobileActions>
          <MessageBadge redirectPath="/partner/messages" />
          <NotificationDropdown />
        </MobileActions>
      </MobileNavbar>
      
      <Overlay $isOpen={isSidebarOpen} onClick={closeSidebar} />
      
      <Sidebar $isOpen={isSidebarOpen}>
        <Logo>Partner Portal</Logo>
        <NavMenu>
          <NavItem>
            <NavLink to="/partner" $active={location.pathname === '/partner'} onClick={closeSidebar}>
              <IconWrapper><FaHome /></IconWrapper>
              Dashboard
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/partner/clients" $active={isPathActive('/partner/clients')} onClick={closeSidebar}>
              <IconWrapper><FaUsers /></IconWrapper>
              Clients
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/partner/plans" $active={isPathActive('/partner/plans')} onClick={closeSidebar}>
              <IconWrapper><FaClipboardList /></IconWrapper>
              Plans
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/partner/consultations" $active={isPathActive('/partner/consultations')} onClick={closeSidebar}>
              <IconWrapper><FaCalendarAlt /></IconWrapper>
              Appointments
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/partner/messages" $active={isPathActive('/partner/messages')} onClick={closeSidebar}>
              <IconWrapper><FaComments /></IconWrapper>
              Messages
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/partner/profile" $active={isPathActive('/partner/profile')} onClick={closeSidebar}>
              <IconWrapper><FaUser /></IconWrapper>
              Profile
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/partner/videos" $active={isPathActive('/partner/videos')} onClick={closeSidebar}>
              <IconWrapper><FaVideo /></IconWrapper>
              Videos
            </NavLink>
          </NavItem>
          
          <NavItem>
            <NavLink to="/partner/support" $active={isPathActive('/partner/support')} onClick={closeSidebar}>
              <IconWrapper><FaQuestionCircle /></IconWrapper>
              Support
            </NavLink>
          </NavItem>
        </NavMenu>
        
        <FooterSection>
          <SignOutButton onClick={handleLogout}>
            <IconWrapper><FaSignOutAlt /></IconWrapper>
            Sign Out
          </SignOutButton>
        </FooterSection>
      </Sidebar>
      
      <Content>
        <ContentHeader>
          <Header />
        </ContentHeader>
        <ContentBody>
          {children}
        </ContentBody>
      </Content>
    </LayoutContainer>
  );
};

export default PartnerLayout; 